import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, FileText, Loader2, Pencil, Search, Trash2, Users } from 'lucide-react';
import ViewBill from '@/components/billing/ViewBill';
import { toast } from 'sonner';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { useChainReportHospitalScope } from '@/hooks/useChainReportHospitalScope';
import { ReportHospitalFilter } from '@/components/reports/ReportHospitalFilter';
import { useAuth } from '@/contexts/AuthContext';
import {
  businessReportsApi,
  type BillWiseCategory,
  type BillWiseRow,
} from '@/api/businessReports';
import { billingApi } from '@/api/billing';
import { ipdBillingApi } from '@/api/ipdBilling';
import { patientApi } from '@/api/patient';
import { downloadBlob } from '@/lib/downloadBlob';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Patient } from '@/types/types';

/** Categories shown in this report (matches product request). */
const CATEGORY_OPTIONS: { value: BillWiseCategory; label: string }[] = [
  { value: 'All', label: 'All categories' },
  { value: 'OPD', label: 'OPD' },
  { value: 'Lab', label: 'Lab' },
  { value: 'Pharma', label: 'Pharma' },
  { value: 'Day Care', label: 'Daycare' },
  { value: 'Additional Services', label: 'Additional Services' },
  { value: 'IPD', label: 'IPD' },
  { value: 'ICU', label: 'ICU' },
  { value: 'IPD Pharma', label: 'IPD Pharma' },
];

function isIpdFinalBillCategory(category: string): boolean {
  return category === 'IPD' || category === 'ICU';
}

/** Outpatient bills editable via ViewBill (not IPD/ICU final bills). */
function canAdminEditBillRow(row: BillWiseRow): boolean {
  if (isIpdFinalBillCategory(row.billCategory)) return false;
  return (
    row.billCategory === 'OPD' ||
    row.billCategory === 'Lab' ||
    row.billCategory === 'Pharma' ||
    row.billCategory === 'Day Care' ||
    row.billCategory === 'Additional Services' ||
    row.billCategory === 'IPD Pharma'
  );
}

function patientGroupKey(row: BillWiseRow): string {
  return `${row.uhid || row.patientName}::${row.patientName}`;
}

type PatientGroup = {
  key: string;
  patientName: string;
  uhid: string | null;
  phone: string | null;
  bills: BillWiseRow[];
  netTotal: number;
};

function matchesPatientSearch(group: PatientGroup, q: string): boolean {
  if (!q) return true;
  const hay = `${group.patientName} ${group.uhid || ''} ${group.phone || ''}`.toLowerCase();
  return hay.includes(q);
}

function isPhoneQuery(term: string): boolean {
  const digits = term.replace(/\D/g, '');
  return digits.length >= 10 && digits.length === term.replace(/\s/g, '').length;
}

/** UHID-like: letters + digits, typically TRRU… / TH… */
function looksLikeUhid(term: string): boolean {
  const t = term.trim();
  return /^[A-Za-z]{2,}\d/.test(t) || /^[A-Za-z]{2,}[A-Za-z0-9]{4,}$/.test(t);
}

function groupBills(bills: BillWiseRow[]): PatientGroup[] {
  const map = new Map<string, PatientGroup>();
  for (const bill of bills) {
    const key = patientGroupKey(bill);
    const existing = map.get(key);
    if (existing) {
      existing.bills.push(bill);
      existing.netTotal += Number(bill.netAmount || 0);
    } else {
      map.set(key, {
        key,
        patientName: bill.patientName,
        uhid: bill.uhid ?? null,
        phone: bill.phone ?? null,
        bills: [bill],
        netTotal: Number(bill.netAmount || 0),
      });
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.patientName.localeCompare(b.patientName, 'en', { sensitivity: 'base' })
  );
}

async function resolvePatientsForSearch(term: string): Promise<Patient[]> {
  if (isPhoneQuery(term)) {
    return (await patientApi.getPatientByPhone(term.replace(/\D/g, ''))) ?? [];
  }
  if (looksLikeUhid(term)) {
    try {
      const byUhid = await patientApi.getPatientByUhid(term.trim());
      if (byUhid.length) return byUhid;
    } catch {
      /* fall through to name/UHID contains search */
    }
  }
  return (await patientApi.getPatientByName(term.trim())) ?? [];
}

export default function PatientWiseBillsReportPage() {
  const { user } = useAuth();
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: 'consolidated' });
  const showHospitalColumn =
    showHospitalFilter &&
    (selectedHospitalId === 'all' || (isSuperAdmin && !selectedHospitalId));
  const queryClient = useQueryClient();
  const canAdminBillActions =
    user?.role === 'HOSPITAL_ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'BUSINESS_ANALYST';

  const [category, setCategory] = useState<BillWiseCategory>('All');
  const [patientSearch, setPatientSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editBillId, setEditBillId] = useState<string | null>(null);
  const {
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange,
  } = useReportDateRange('this_month');

  const searchTerm = patientSearch.trim();
  /** When searching a specific patient, load all-time bills (same as receptionist Patient Bills). */
  const patientLookupActive = searchTerm.length >= 3;

  const { data: report, isLoading: periodLoading } = useQuery({
    queryKey: ['patient-wise-bills', fromIso, toIso, category, reportHospitalId ?? 'self'],
    queryFn: () =>
      businessReportsApi.billWiseReport({
        from: fromIso!,
        to: toIso!,
        category,
        hospitalId: reportHospitalId,
      }),
    enabled: !!fromIso && !!toIso && !patientLookupActive,
  });

  const {
    data: patientLookup,
    isLoading: patientLookupLoading,
    isFetching: patientLookupFetching,
    error: patientLookupError,
  } = useQuery({
    queryKey: ['patient-wise-bills-lookup', searchTerm, category, reportHospitalId ?? 'self'],
    queryFn: async () => {
      const patients = await resolvePatientsForSearch(searchTerm);
      if (!patients.length) {
        return { patients: [] as Patient[], bills: [] as BillWiseRow[] };
      }
      const reports = await Promise.all(
        patients.slice(0, 8).map((p) =>
          billingApi
            .getPatientAllBills(p.id, {
              category,
              ...(reportHospitalId ? { hospitalId: reportHospitalId } : {}),
            })
            .catch(() => null)
        )
      );
      const bills = reports.flatMap((r) => r?.bills ?? []);
      return { patients, bills };
    },
    enabled: patientLookupActive,
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: async (row: BillWiseRow) => {
      if (isIpdFinalBillCategory(row.billCategory)) {
        return ipdBillingApi.deleteIpdBill(row.billId, {
          confirmBillNumber: row.billNumber,
        });
      }
      return billingApi.deleteBill(row.billId, {
        confirmBillNumber: row.billNumber,
      });
    },
    onSuccess: (res) => {
      const msg =
        (res as { data?: { message?: string }; message?: string })?.data?.message ||
        (res as { message?: string })?.message ||
        'Bill deleted';
      toast.success(msg);
      queryClient.invalidateQueries({ queryKey: ['patient-wise-bills'] });
      queryClient.invalidateQueries({ queryKey: ['patient-wise-bills-lookup'] });
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || 'Failed to delete bill');
    },
    onSettled: () => setDeletingId(null),
  });

  const periodGroups = useMemo(
    () => groupBills(report?.bills ?? []),
    [report?.bills]
  );

  const lookupGroups = useMemo(
    () => groupBills(patientLookup?.bills ?? []),
    [patientLookup?.bills]
  );

  const searchQ = searchTerm.toLowerCase();
  const filteredGroups = useMemo(() => {
    if (patientLookupActive) return lookupGroups;
    return periodGroups.filter((g) => matchesPatientSearch(g, searchQ));
  }, [patientLookupActive, lookupGroups, periodGroups, searchQ]);

  const isLoading = patientLookupActive
    ? patientLookupLoading || patientLookupFetching
    : periodLoading;

  const filteredTotals = useMemo(() => {
    const bills = filteredGroups.flatMap((g) => g.bills);
    return {
      patients: filteredGroups.length,
      count: bills.length,
      netAmount: bills.reduce((s, b) => s + Number(b.netAmount || 0), 0),
      paidAmount: bills.reduce((s, b) => s + Number(b.paidAmount || 0), 0),
    };
  }, [filteredGroups]);

  const dateSuffix =
    fromIso && toIso
      ? `${format(new Date(fromIso), 'yyyy-MM-dd')}-to-${format(new Date(toIso), 'yyyy-MM-dd')}`
      : 'report';

  const handleDownloadPdf = async (row: BillWiseRow) => {
    setDownloadingId(row.billId);
    try {
      if (isIpdFinalBillCategory(row.billCategory)) {
        if (!row.admissionId) {
          toast.error('IPD admission not found for this bill');
          return;
        }
        const resp = await ipdBillingApi.getFinalBillPDF(row.admissionId);
        downloadBlob(
          new Blob([resp.data], { type: 'application/pdf' }),
          `IPD-Bill-${row.billNumber || row.ipdNumber || row.billId}.pdf`
        );
      } else {
        const resp = await billingApi.exportBillPDF(row.billId);
        downloadBlob(
          new Blob([resp.data], { type: 'application/pdf' }),
          `Bill-${row.billNumber || row.billId}.pdf`
        );
      }
      toast.success('Bill PDF downloaded');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || 'Failed to download bill PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteBill = (row: BillWiseRow) => {
    const isIpd = isIpdFinalBillCategory(row.billCategory);
    const ok = window.confirm(
      isIpd
        ? `Delete IPD bill ${row.billNumber}?\n\nThis permanently removes the bill document and its lines. The admission stay, deposits, and ward pharmacy stock are kept (re-accrue if needed).`
        : `Delete bill ${row.billNumber} (${row.billCategory})?\n\nThis permanently removes the bill, payments, and related billing data. Pharma medicines on this bill are returned to inventory. Reports will update automatically.`
    );
    if (!ok) return;
    const typed = window.prompt(
      `Type the bill number exactly to confirm deletion:\n${row.billNumber}`,
      ''
    );
    if (!typed || typed.trim().toUpperCase() !== String(row.billNumber).trim().toUpperCase()) {
      toast.error('Bill number did not match — delete cancelled');
      return;
    }
    setDeletingId(row.billId);
    deleteMut.mutate(row);
  };

  const handleExportCsv = async () => {
    if (!fromIso || !toIso) return;
    setExportingCsv(true);
    try {
      const resp = await businessReportsApi.billWiseReportExport({
        from: fromIso,
        to: toIso,
        category,
        hospitalId: reportHospitalId,
      });
      const cat = category === 'All' ? 'all' : category.replace(/\s+/g, '-').toLowerCase();
      downloadBlob(
        new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
        `Patient-wise-bills-${cat}-${dateSuffix}.csv`
      );
      toast.success('CSV download started');
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || 'CSV export failed');
    } finally {
      setExportingCsv(false);
    }
  };

  const emptyMessage = (() => {
    if (patientLookupActive) {
      if (patientLookupError) return 'Patient search failed. Check the UHID/name and try again.';
      if ((patientLookup?.patients.length ?? 0) === 0) {
        return `No patient found for “${searchTerm}”. Check UHID spelling (e.g. TRRU… vs TRRJ…).`;
      }
      return `Patient found, but no bills for category “${category}”.`;
    }
    return 'No bills found for this date range and category. Tip: receptionist Patient Bills shows all years; here pick the bill year (e.g. 2023) or search by exact UHID/name (3+ characters) to load all-time bills.';
  })();

  return (
    <div className="thms-page-shell">
      <div>
        <h1 className="thms-page-title flex items-center gap-2">
          <Users className="w-8 h-8 text-blue-600" />
          Patient-wise Bills
        </h1>
        <p className="thms-page-subtitle">
          Period view lists bills in the selected date range. Search by name, UHID, or phone (3+
          characters) to load that patient’s bills for all time — same as receptionist Patient Bills.
          {canAdminBillActions
            ? ' Hospital admins can edit outpatient bill lines (amounts / payment mode) or permanently delete a bill (pharma stock is restored on delete).'
            : ''}
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <TimeIntervalFilter
            onTimeIntervalChange={handleTimeIntervalChange}
            selectedInterval={selectedInterval}
            onIntervalChange={setSelectedInterval}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
          <div className="flex flex-wrap items-end gap-3">
            {showHospitalFilter ? (
              <ReportHospitalFilter
                hospitals={hospitalOptions}
                value={selectedHospitalId}
                onChange={setSelectedHospitalId}
                isSuperAdmin={isSuperAdmin}
              />
            ) : null}
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search patient (name, UHID, phone)"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
              />
            </div>
            <Select value={category} onValueChange={(v) => setCategory(v as BillWiseCategory)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              disabled={!fromIso || !toIso || exportingCsv || patientLookupActive}
              onClick={handleExportCsv}
              title={
                patientLookupActive
                  ? 'CSV export uses the period report — clear search to export'
                  : undefined
              }
            >
              {exportingCsv ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exportingCsv ? 'Exporting…' : 'Download CSV'}
            </Button>
          </div>
          {patientLookupActive ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Patient lookup mode: showing <strong>all-time</strong> bills for matches of “
              {searchTerm}” (date filter ignored). Clear the search to return to the period report.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {!isLoading && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'Patients', value: filteredTotals.patients, format: 'integer' as const },
            { label: 'Bills', value: filteredTotals.count, format: 'integer' as const },
            { label: 'Net amount', value: filteredTotals.netAmount, format: 'currency' as const },
            { label: 'Paid amount', value: filteredTotals.paidAmount, format: 'currency' as const },
          ].map((t) => (
            <Card key={t.label}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t.label}</p>
                <p className="mt-1 text-xl font-semibold tabular-nums">
                  {t.format === 'currency'
                    ? `₹${t.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                    : t.value.toLocaleString('en-IN')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          {patientLookupActive ? 'Looking up patient bills…' : 'Loading bills…'}
        </p>
      ) : filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">{emptyMessage}</CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <Card key={group.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span>{group.patientName}</span>
                  <span className="text-sm font-normal text-muted-foreground">
                    UHID: {group.uhid || '—'}
                    {group.phone ? ` · ${group.phone}` : ''}
                  </span>
                  <span className="ml-auto text-sm font-medium tabular-nums text-gray-700">
                    {group.bills.length} bill{group.bills.length === 1 ? '' : 's'} · ₹
                    {group.netTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        {showHospitalColumn ? <TableHead>Hospital</TableHead> : null}
                        <TableHead>Bill no.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>IPD no.</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.bills.map((row) => (
                        <TableRow key={`${row.billCategory}-${row.billId}`}>
                          <TableCell className="whitespace-nowrap">{row.billCategory}</TableCell>
                          {showHospitalColumn ? (
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {row.hospitalName || '—'}
                            </TableCell>
                          ) : null}
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {row.billNumber}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(row.billDate), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            ₹{row.netAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            ₹{row.paidAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{row.status}</TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {row.ipdNumber || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={
                                  downloadingId === row.billId ||
                                  (isIpdFinalBillCategory(row.billCategory) && !row.admissionId)
                                }
                                onClick={() => handleDownloadPdf(row)}
                              >
                                {downloadingId === row.billId ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <FileText className="mr-1.5 h-4 w-4" />
                                    Download
                                  </>
                                )}
                              </Button>
                              {canAdminBillActions && canAdminEditBillRow(row) ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEditBillId(row.billId)}
                                >
                                  <Pencil className="mr-1.5 h-4 w-4" />
                                  Edit
                                </Button>
                              ) : null}
                              {canAdminBillActions ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-destructive border-destructive/40 hover:bg-destructive/10"
                                  disabled={deletingId === row.billId || deleteMut.isPending}
                                  onClick={() => handleDeleteBill(row)}
                                >
                                  {deletingId === row.billId ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Trash2 className="mr-1.5 h-4 w-4" />
                                      Delete
                                    </>
                                  )}
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ViewBill
        billId={editBillId}
        isOpen={!!editBillId}
        onClose={() => setEditBillId(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['patient-wise-bills'] });
          queryClient.invalidateQueries({ queryKey: ['patient-wise-bills-lookup'] });
        }}
      />
    </div>
  );
}
